import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/contexts/SettingsContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, Type, Building, Save, MessageCircle, LineChart, Send, CheckCircle2, Image as ImageIcon, Building2, ListChecks, Plus, X } from "lucide-react";

const FONTS = ["Playfair Display", "Outfit", "Manrope", "Space Grotesk", "DM Serif Display", "Inter"];
const FIELD_TYPES = [
  { v: "text", l: "Metin" }, { v: "number", l: "Sayı" }, { v: "tel", l: "Telefon" },
  { v: "email", l: "E-posta" }, { v: "date", l: "Tarih" }, { v: "textarea", l: "Uzun Metin" },
];

function newId() { return "id_" + Math.random().toString(36).slice(2, 10); }

export default function SettingsPage() {
  const { reload } = useSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/settings/full").then((r) => setForm(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Ayarlar güncellendi");
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const sendExpiry = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send_expiry");
      toast.success(`Uyarı gönderildi: ${data.sent} — Atlanan: ${data.skipped}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const ColorInput = ({ k, label }) => (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={form[k] || "#000000"} onChange={(e) => set(k, e.target.value)}
          className="w-12 h-10 rounded border border-[color:var(--asg-border)] bg-transparent cursor-pointer" data-testid={`settings-color-${k}`} />
        <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] flex-1 font-mono text-sm" />
      </div>
    </div>
  );

  const FontSelect = ({ k, label }) => (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <select value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
        className="bg-[color:var(--asg-surface)] border border-[color:var(--asg-border)] rounded-md h-10 px-3 text-[color:var(--asg-text)] text-sm" data-testid={`settings-font-${k}`}>
        {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
    </div>
  );

  // Slider helpers
  const slides = Array.isArray(form.slider_images) ? form.slider_images : [];
  const addSlide = () => set("slider_images", [...slides, { url: "", title: "", subtitle: "" }]);
  const updateSlide = (idx, k, v) => set("slider_images", slides.map((s, i) => i === idx ? { ...s, [k]: v } : s));
  const removeSlide = (idx) => set("slider_images", slides.filter((_, i) => i !== idx));

  // Companies
  const companies = Array.isArray(form.insurance_companies) ? form.insurance_companies : [];
  const addCompany = () => set("insurance_companies", [...companies, { id: newId(), name: "", logo_url: "" }]);
  const updateCompany = (idx, k, v) => set("insurance_companies", companies.map((c, i) => i === idx ? { ...c, [k]: v } : c));
  const removeCompany = (idx) => set("insurance_companies", companies.filter((_, i) => i !== idx));

  // Custom policy fields
  const customFields = Array.isArray(form.policy_custom_fields) ? form.policy_custom_fields : [];
  const addField = () => set("policy_custom_fields", [...customFields, { name: `alan_${customFields.length + 1}`, label: "Yeni Alan", type: "text", required: false }]);
  const updateField = (idx, k, v) => set("policy_custom_fields", customFields.map((f, i) => i === idx ? { ...f, [k]: v } : f));
  const removeField = (idx) => set("policy_custom_fields", customFields.filter((_, i) => i !== idx));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl">Site Ayarları</h1>
          <p className="text-[color:var(--asg-muted)] text-sm mt-1">İçerik, tema, entegrasyonlar, şirketler ve poliçe alanları tek yerden.</p>
        </div>
        <Button onClick={save} disabled={saving} className="asg-btn-primary rounded-full border-0" data-testid="settings-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Kaydediliyor..." : "Tüm Ayarları Kaydet"}
        </Button>
      </div>

      <Tabs defaultValue="business" className="w-full">
        <TabsList className="bg-[color:var(--asg-surface)] border border-[color:var(--asg-border)] mb-6 h-auto flex flex-wrap p-1">
          <TabsTrigger value="business" data-testid="tab-business"><Building className="w-4 h-4 mr-2" />İşletme</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content"><Type className="w-4 h-4 mr-2" />İçerik</TabsTrigger>
          <TabsTrigger value="slider" data-testid="tab-slider"><ImageIcon className="w-4 h-4 mr-2" />Slider</TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies"><Building2 className="w-4 h-4 mr-2" />Sigorta Şirketleri</TabsTrigger>
          <TabsTrigger value="policy_fields" data-testid="tab-policy-fields"><ListChecks className="w-4 h-4 mr-2" />Poliçe Alanları</TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme"><Palette className="w-4 h-4 mr-2" />Tema</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking"><LineChart className="w-4 h-4 mr-2" />Reklam</TabsTrigger>
        </TabsList>

        <TabsContent value="business" className="asg-card rounded-2xl p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2"><Label>Site Adı</Label><Input value={form.site_name || ""} onChange={(e) => set("site_name", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
            <div className="grid gap-2"><Label>Slogan</Label><Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
            <div className="grid gap-2"><Label>Telefon</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" data-testid="settings-phone" /></div>
            <div className="grid gap-2"><Label>WhatsApp Numarası</Label><Input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" data-testid="settings-whatsapp" /></div>
            <div className="grid gap-2"><Label>E-posta</Label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
            <div className="grid gap-2"><Label>Çalışma Saatleri</Label><Input value={form.working_hours || ""} onChange={(e) => set("working_hours", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
            <div className="sm:col-span-2 grid gap-2"><Label>Adres</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
            <div className="sm:col-span-2 grid gap-2"><Label>Logo URL</Label><Input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] font-mono text-sm" /></div>
          </div>
        </TabsContent>

        <TabsContent value="content" className="asg-card rounded-2xl p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 grid gap-2"><Label>Hero Başlığı</Label><Input value={form.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
            <div className="sm:col-span-2 grid gap-2"><Label>Hero Alt Başlık</Label><Textarea rows={3} value={form.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
            <div className="sm:col-span-2 grid gap-2"><Label>Hakkımızda Metni</Label><Textarea rows={5} value={form.about_text || ""} onChange={(e) => set("about_text", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" /></div>
          </div>
        </TabsContent>

        <TabsContent value="slider" className="asg-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className="text-base">Ana Sayfa Slider Görselleri</Label>
              <p className="text-xs text-[color:var(--asg-muted)] mt-1">Turkish Airlines tarzı yarım-sayfa slider. Her görsel ~6 saniyede döner.</p>
            </div>
            <Button size="sm" variant="outline" onClick={addSlide} className="border-[color:var(--asg-border)] bg-transparent" data-testid="settings-slider-add">
              <Plus className="w-3 h-3 mr-1.5" /> Slayt Ekle
            </Button>
          </div>
          <div className="grid gap-3">
            {slides.map((s, idx) => (
              <div key={idx} className="rounded-xl border border-[color:var(--asg-border)] p-4 grid md:grid-cols-12 gap-3 bg-[color:var(--asg-surface)]" data-testid={`settings-slide-${idx}`}>
                {s.url && (
                  <div className="md:col-span-2">
                    <img src={s.url} alt="preview" className="w-full h-20 object-cover rounded-lg" />
                  </div>
                )}
                <div className="md:col-span-5 grid gap-1">
                  <Label className="text-xs">Görsel URL</Label>
                  <Input value={s.url} onChange={(e) => updateSlide(idx, "url", e.target.value)} className="bg-white border-[color:var(--asg-border)] font-mono text-xs" />
                </div>
                <div className="md:col-span-2 grid gap-1">
                  <Label className="text-xs">Başlık</Label>
                  <Input value={s.title} onChange={(e) => updateSlide(idx, "title", e.target.value)} className="bg-white border-[color:var(--asg-border)]" />
                </div>
                <div className="md:col-span-2 grid gap-1">
                  <Label className="text-xs">Alt Başlık</Label>
                  <Input value={s.subtitle} onChange={(e) => updateSlide(idx, "subtitle", e.target.value)} className="bg-white border-[color:var(--asg-border)]" />
                </div>
                <div className="md:col-span-1 flex items-end justify-end">
                  <button onClick={() => removeSlide(idx)} className="text-zinc-400 hover:text-red-500" data-testid={`settings-slide-remove-${idx}`}><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {slides.length === 0 && <div className="text-center text-sm text-[color:var(--asg-muted)] py-6">Henüz slayt yok — "Slayt Ekle" ile ekleyin.</div>}
          </div>
        </TabsContent>

        <TabsContent value="companies" className="asg-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className="text-base">Sigorta Şirketleri</Label>
              <p className="text-xs text-[color:var(--asg-muted)] mt-1">Poliçelerde kullanılacak sigorta şirketleri listesi.</p>
            </div>
            <Button size="sm" variant="outline" onClick={addCompany} className="border-[color:var(--asg-border)] bg-transparent" data-testid="settings-company-add">
              <Plus className="w-3 h-3 mr-1.5" /> Şirket Ekle
            </Button>
          </div>
          <div className="grid gap-3">
            {companies.map((c, idx) => (
              <div key={c.id || idx} className="rounded-xl border border-[color:var(--asg-border)] p-4 grid md:grid-cols-12 gap-3 bg-[color:var(--asg-surface)]" data-testid={`settings-company-${idx}`}>
                {c.logo_url && (
                  <div className="md:col-span-2">
                    <img src={c.logo_url} alt={c.name} className="w-full h-16 object-contain rounded-lg bg-white p-2 border border-[color:var(--asg-border)]" />
                  </div>
                )}
                <div className={c.logo_url ? "md:col-span-4" : "md:col-span-6"}>
                  <Label className="text-xs">Şirket Adı</Label>
                  <Input value={c.name || ""} onChange={(e) => updateCompany(idx, "name", e.target.value)} className="bg-white border-[color:var(--asg-border)]" data-testid={`settings-company-name-${idx}`} />
                </div>
                <div className="md:col-span-5">
                  <Label className="text-xs">Logo URL (opsiyonel)</Label>
                  <Input value={c.logo_url || ""} onChange={(e) => updateCompany(idx, "logo_url", e.target.value)} className="bg-white border-[color:var(--asg-border)] font-mono text-xs" />
                </div>
                <div className="md:col-span-1 flex items-end justify-end">
                  <button onClick={() => removeCompany(idx)} className="text-zinc-400 hover:text-red-500" data-testid={`settings-company-remove-${idx}`}><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {companies.length === 0 && <div className="text-center text-sm text-[color:var(--asg-muted)] py-6">Henüz şirket yok — "Şirket Ekle" ile ekleyin.</div>}
          </div>
        </TabsContent>

        <TabsContent value="policy_fields" className="asg-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className="text-base">Poliçe Özel Alanları</Label>
              <p className="text-xs text-[color:var(--asg-muted)] mt-1">Yeni poliçe eklerken görüntülenecek özel alanlar (Brüt Prim, Net Prim, Plaka No vb.).</p>
            </div>
            <Button size="sm" variant="outline" onClick={addField} className="border-[color:var(--asg-border)] bg-transparent" data-testid="settings-field-add">
              <Plus className="w-3 h-3 mr-1.5" /> Alan Ekle
            </Button>
          </div>
          <div className="grid gap-3">
            {customFields.map((f, idx) => (
              <div key={idx} className="rounded-xl border border-[color:var(--asg-border)] p-4 grid md:grid-cols-12 gap-3 bg-[color:var(--asg-surface)]" data-testid={`settings-field-${idx}`}>
                <div className="md:col-span-3">
                  <Label className="text-xs">Anahtar (name)</Label>
                  <Input value={f.name} onChange={(e) => updateField(idx, "name", e.target.value.replace(/[^a-z0-9_]/g, "_").toLowerCase())} className="bg-white border-[color:var(--asg-border)] font-mono text-xs" />
                </div>
                <div className="md:col-span-4">
                  <Label className="text-xs">Etiket</Label>
                  <Input value={f.label} onChange={(e) => updateField(idx, "label", e.target.value)} className="bg-white border-[color:var(--asg-border)]" data-testid={`settings-field-label-${idx}`} />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-xs">Tip</Label>
                  <Select value={f.type} onValueChange={(v) => updateField(idx, "type", v)}>
                    <SelectTrigger className="bg-white border-[color:var(--asg-border)] h-9"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-[color:var(--asg-border)]">
                      {FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 flex items-end gap-1">
                  <Switch checked={!!f.required} onCheckedChange={(v) => updateField(idx, "required", v)} />
                </div>
                <div className="md:col-span-1 flex items-end justify-end">
                  <button onClick={() => removeField(idx)} className="text-zinc-400 hover:text-red-500" data-testid={`settings-field-remove-${idx}`}><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {customFields.length === 0 && <div className="text-center text-sm text-[color:var(--asg-muted)] py-6">Henüz özel alan yok — "Alan Ekle" ile ekleyin.</div>}
          </div>
        </TabsContent>

        <TabsContent value="theme" className="asg-card rounded-2xl p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorInput k="bg_color" label="Arka Plan Rengi" />
            <ColorInput k="surface_color" label="Kart/Yüzey Rengi" />
            <ColorInput k="primary_color" label="Vurgu Rengi (Altın)" />
            <ColorInput k="text_color" label="Yazı Rengi" />
            <ColorInput k="muted_color" label="Soluk Yazı Rengi" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <FontSelect k="heading_font" label="Başlık Fontu" />
            <FontSelect k="body_font" label="Metin Fontu" />
          </div>
          <p className="text-xs text-[color:var(--asg-muted)] mt-4">💡 Değişiklikleri kaydeder kaydetmez tüm site anında güncellenir.</p>
        </TabsContent>

        <TabsContent value="whatsapp" className="asg-card rounded-2xl p-6">
          <div className="mb-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
            <strong>Twilio WhatsApp Business API</strong> ile yeni talep geldiğinde ve poliçe süresi 3 gün kala <em>sadece admin telefonuna</em> otomatik mesaj gönderilir.
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2"><Label>Twilio Account SID</Label>
              <Input value={form.twilio_account_sid || ""} onChange={(e) => set("twilio_account_sid", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] font-mono text-sm" placeholder="ACxxxxxxxxxxxxxxxxxx" data-testid="settings-twilio-sid" />
            </div>
            <div className="grid gap-2"><Label>Twilio Auth Token</Label>
              <Input type="password" value={form.twilio_auth_token || ""} onChange={(e) => set("twilio_auth_token", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] font-mono text-sm" placeholder="••••••••" data-testid="settings-twilio-token" />
            </div>
            <div className="grid gap-2"><Label>Twilio WhatsApp Numarası</Label>
              <Input value={form.twilio_whatsapp_from || ""} onChange={(e) => set("twilio_whatsapp_from", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" placeholder="whatsapp:+14155238886" data-testid="settings-twilio-from" />
            </div>
            <div className="grid gap-2"><Label>Bildirim Alacak Admin Telefonu</Label>
              <Input value={form.notification_admin_phone || ""} onChange={(e) => set("notification_admin_phone", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" placeholder="+90 5XX XXX XX XX" data-testid="settings-admin-phone" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-3 mt-2 p-3 rounded-lg bg-[color:var(--asg-surface)]">
              <Switch checked={!!form.whatsapp_notifications_enabled} onCheckedChange={(v) => set("whatsapp_notifications_enabled", v)} data-testid="settings-whatsapp-enabled" />
              <div>
                <Label>WhatsApp Bildirimleri Aktif</Label>
                <p className="text-xs text-[color:var(--asg-muted)]">Kapatıldığında hiç mesaj gönderilmez.</p>
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3 border-t border-[color:var(--asg-border)] pt-5">
            <Button onClick={save} disabled={saving} className="asg-btn-primary rounded-full border-0">
              <Save className="w-4 h-4 mr-2" /> Bilgileri Kaydet
            </Button>
            <Button onClick={sendExpiry} disabled={sending} variant="outline" className="rounded-full border-[color:var(--asg-border)] bg-transparent" data-testid="settings-send-expiry-btn">
              <Send className="w-4 h-4 mr-2" /> {sending ? "Gönderiliyor..." : "Şimdi Yaklaşan Uyarıları Gönder"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="tracking" className="asg-card rounded-2xl p-6">
          <div className="mb-5 p-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-sm">
            <strong>UTM parametreleri</strong> her talep ile birlikte otomatik kaydedilir. <strong>Google Ads / Analytics</strong> ID'lerini girerseniz siteye ilgili takip kodu otomatik yüklenir.
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2"><Label>Google Ads ID</Label>
              <Input value={form.google_ads_id || ""} onChange={(e) => set("google_ads_id", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] font-mono text-sm" placeholder="AW-123456789" data-testid="settings-google-ads-id" />
            </div>
            <div className="grid gap-2"><Label>Google Ads Conversion Label</Label>
              <Input value={form.google_ads_conversion_label || ""} onChange={(e) => set("google_ads_conversion_label", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] font-mono text-sm" placeholder="abcd1234EfGh" data-testid="settings-google-ads-label" />
            </div>
            <div className="sm:col-span-2 grid gap-2"><Label>Google Analytics 4 Measurement ID</Label>
              <Input value={form.google_analytics_id || ""} onChange={(e) => set("google_analytics_id", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] font-mono text-sm" placeholder="G-XXXXXXXXXX" data-testid="settings-google-analytics-id" />
            </div>
          </div>
          <div className="mt-5">
            <Button onClick={save} disabled={saving} className="asg-btn-primary rounded-full border-0">
              <Save className="w-4 h-4 mr-2" /> Takip Ayarlarını Kaydet
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
